import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { getInningScores } from '../gameEngine';

export default function SpectatorView({ game: initialGame, navigate }) {
  const [game, setGame] = useState(initialGame);
  const [inningScores, setInningScores] = useState({});

  // Load inning scores
  useEffect(() => {
    getInningScores(game.id).then(rows => {
      const scores = {};
      rows.forEach(r => {
        if (!scores[r.inning]) scores[r.inning] = {};
        scores[r.inning][r.team] = r.runs;
      });
      setInningScores(scores);
    });
  }, [game.id]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel(`game-${game.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'games',
        filter: `id=eq.${game.id}`,
      }, payload => {
        setGame(payload.new);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inning_scores',
        filter: `game_id=eq.${game.id}`,
      }, payload => {
        const r = payload.new;
        if (r) {
          setInningScores(s => ({
            ...s,
            [r.inning]: { ...s[r.inning], [r.team]: r.runs }
          }));
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [game.id]);

  const inningLabel = `${game.is_top_inning ? '▲ TOP' : '▼ BOT'} ${game.current_inning}`;
  const battingTeam = game.is_top_inning ? game.away_team_name : game.home_team_name;

  if (game.status === 'final') {
    const winner = game.winner === 'home' ? game.home_team_name : game.winner === 'away' ? game.away_team_name : null;
    return (
      <div className="screen">
        <div className="header">
          <button className="header-back" onClick={() => navigate('home')}>‹</button>
          <span className="header-title">Final Score</span>
        </div>
        <div className="game-over">
          <div className="game-over-title">Game Over</div>
          <div className="game-over-score">{game.away_score} – {game.home_score}</div>
          <div className="game-over-teams">
            <span>{game.away_team_name}</span>
            <span style={{ color: 'var(--border)' }}>vs</span>
            <span>{game.home_team_name}</span>
          </div>
          {winner && <div className="game-over-winner">🏆 {winner} Wins!</div>}
          {game.winner === 'tie' && <div className="game-over-winner">It's a Tie!</div>}
          <button className="btn btn-ghost" onClick={() => navigate('home')} style={{ marginTop: 24, width: '100%' }}>Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="header">
        <button className="header-back" onClick={() => navigate('home')}>‹</button>
        <span className="header-title">⚾ Live Game</span>
        <div style={{
          background: 'var(--green-dim)',
          border: '1px solid var(--green)',
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 2,
          color: 'var(--green)',
          padding: '3px 10px',
          textTransform: 'uppercase',
          animation: 'pulse 2s ease-in-out infinite'
        }}>LIVE</div>
      </div>

      <div className="spectator-badge">👁 Spectator View — Read Only</div>

      <div className="scroll-content" style={{ paddingTop: 8 }}>
        {/* Scoreboard */}
        <div className="scoreboard">
          <div className="scoreboard-inning">{inningLabel} &nbsp;·&nbsp; {battingTeam} Batting</div>
          <div className="scoreboard-teams">
            <div className="scoreboard-row">
              <span className="team-label">AWAY</span>
              <span className="team-name">{game.away_team_name}</span>
              <span className={`team-score ${game.is_top_inning ? 'batting' : ''}`}>{game.away_score}</span>
            </div>
            <div className="scoreboard-row">
              <span className="team-label">HOME</span>
              <span className="team-name">{game.home_team_name}</span>
              <span className={`team-score ${!game.is_top_inning ? 'batting' : ''}`}>{game.home_score}</span>
            </div>
          </div>
        </div>

        {/* Count */}
        <div className="count-bar">
          <div className="count-item">
            <span className="count-label">Balls</span>
            <span className="count-val balls">{game.balls}</span>
          </div>
          <div className="count-item">
            <span className="count-label">Strikes</span>
            <span className="count-val strikes">{game.strikes}</span>
          </div>
          <div className="count-item">
            <span className="count-label">Outs</span>
            <span className="count-val outs">{game.outs}</span>
          </div>
        </div>

        {/* Bases */}
        <div className="bases-container">
          <div className="bases-title">Bases</div>
          <div className="bases-diamond">
            <div className={`base base-2nd readonly ${game.base_2 ? 'occupied' : ''}`} />
            <div className={`base base-3rd readonly ${game.base_3 ? 'occupied' : ''}`} />
            <div className={`base base-1st readonly ${game.base_1 ? 'occupied' : ''}`} />
            <div className="base base-home" />
          </div>
        </div>

        {/* Inning by inning */}
        {Object.keys(inningScores).length > 0 && (
          <div style={{ margin: '8px 16px', background: 'var(--surface)', border: '2px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div style={{ background: 'var(--green-dim)', padding: '8px 16px', fontSize: 11, letterSpacing: 2, color: 'var(--green)', fontWeight: 700, textTransform: 'uppercase' }}>
              Inning by Inning
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    <td style={{ padding: '8px 12px', color: 'var(--gray)', fontWeight: 700, fontSize: 11, letterSpacing: 1 }}>TEAM</td>
                    {Array.from({ length: game.current_inning }, (_, i) => (
                      <td key={i + 1} style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--gray)', fontWeight: 700, fontSize: 11 }}>{i + 1}</td>
                    ))}
                    <td style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>R</td>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: 13 }}>{game.away_team_name}</td>
                    {Array.from({ length: game.current_inning }, (_, i) => (
                      <td key={i + 1} style={{ padding: '10px', textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: 'var(--white)' }}>
                        {inningScores[i + 1]?.away ?? (i + 1 < game.current_inning || !game.is_top_inning ? '0' : '-')}
                      </td>
                    ))}
                    <td style={{ padding: '10px', textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: 'var(--green)' }}>{game.away_score}</td>
                  </tr>
                  <tr style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: 13 }}>{game.home_team_name}</td>
                    {Array.from({ length: game.current_inning }, (_, i) => (
                      <td key={i + 1} style={{ padding: '10px', textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: 'var(--white)' }}>
                        {inningScores[i + 1]?.home ?? (i + 1 < game.current_inning ? '0' : '-')}
                      </td>
                    ))}
                    <td style={{ padding: '10px', textAlign: 'center', fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: 'var(--green)' }}>{game.home_score}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Game info */}
        {game.umpire_name && (
          <div style={{ textAlign: 'center', padding: '8px 16px', fontSize: 12, color: 'var(--gray)', letterSpacing: 1 }}>
            Umpire: {game.umpire_name}
          </div>
        )}
      </div>
    </div>
  );
}
